<template>
    <div class="vehicle-selection container p-4 mb-4">
        <h4 class="text-center mb-4">
            <i class="fas fa-car mr-2"></i>
            Escolha seu veículo
        </h4>

        <!-- Seletor de origem do veículo -->
        <div class="vehicle-type-selector">
            <button
                type="button"
                class="type-card"
                :class="{ active: selectedOrigin === 'not_shared' }"
                @click="changeOrigin('not_shared')"
            >
                <i class="fas fa-car type-card-icon"></i>
                <span class="type-card-label">Centro de Custo</span>
            </button>
            <button
                type="button"
                class="type-card"
                :class="{ active: selectedOrigin === 'is_shared' }"
                @click="changeOrigin('is_shared')"
            >
                <i class="fas fa-share-alt type-card-icon"></i>
                <span class="type-card-label">Compartilhados</span>
            </button>
        </div>

        <!-- Veículos do Centro de Custo -->
        <VehicleSection
            v-if="selectedOrigin === 'not_shared'"
            title="Veículos do seu Centro de Custo"
            icon="fas fa-car"
            :vehicles="vehicles.not_shared"
            :selected-vehicle-id="selectedVehicleId"
            :is-consultation-only="isConsultationOnly"
            :show-consultation-message="isConsultationOnly"
            :requires-approver="false"
            :is-not-shared="true"
            consultation-message="Modo consulta: visualização apenas"
            @select-vehicle="selectVehicle"
            @show-details="showVehicleDetails"
        />

        <!-- Veículos Compartilhados -->
        <VehicleSection
            v-else
            title="Veículos Compartilhados"
            icon="fas fa-share-alt"
            :vehicles="vehicles.is_shared"
            :selected-vehicle-id="selectedVehicleId"
            :is-consultation-only="isConsultationOnly"
            :show-consultation-message="isConsultationOnly"
            :requires-approver="true"
            :is-not-shared="false"
            consultation-message="Modo consulta: visualização apenas"
            @select-vehicle="selectVehicle"
            @show-details="showVehicleDetails"
        />

        <!-- Mensagem quando não há veículos -->
        <div v-if="!hasAnyVehicles" class="no-vehicles-message">
            <div class="alert alert-info text-center">
                <i class="fas fa-info-circle mr-2"></i>
                Nenhum veículo disponível para os critérios selecionados.
                <br>
                <small>Tente ajustar os filtros de pesquisa.</small>
            </div>
        </div>
    </div>
</template>

<script>
import VehicleSection from './VehicleSection.vue';

export default {
    name: 'VehicleSelection',
    components: {
        VehicleSection,
    },
    props: {
        vehicles: {
            type: Object,
            default: () => ({ is_shared: [], not_shared: [] })
        },
        selectedVehicleId: {
            type: [String, Number],
            default: ''
        },
        isConsultationOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            // Origem do veículo exibida. Inicia sempre em "Centro de Custo",
            // replicando o comportamento do app Flutter.
            selectedOrigin: 'not_shared'
        };
    },
    computed: {
        hasAnyVehicles() {
            return (this.vehicles.not_shared && this.vehicles.not_shared.length > 0) ||
                (this.vehicles.is_shared && this.vehicles.is_shared.length > 0);
        }
    },
    methods: {
        changeOrigin(origin) {
            if (this.selectedOrigin === origin) {
                return;
            }

            this.selectedOrigin = origin;

            // Ao trocar de origem, desfaz a seleção de veículo atual — equivalente ao
            // setSelectedCar(null, null) do Flutter — EXCETO quando o veículo selecionado
            // pertence à nova origem (ex: reserva em edição com veículo compartilhado),
            // pois nesse caso ele passa a ficar visível e não deve ser apagado.
            if (this.selectedVehicleId && !this.originContainsSelectedVehicle(origin)) {
                this.$emit('select-vehicle', {
                    id: '',
                    places: 0,
                    is_executive: false,
                    requiresApprover: false
                });
            }
        },

        originContainsSelectedVehicle(origin) {
            const list = this.vehicles[origin] || [];
            return list.some(vehicle => vehicle.id === this.selectedVehicleId);
        },

        selectVehicle(vehicle, requiresApprover = false) {
            this.$emit('select-vehicle', {
                id: vehicle.id,
                places: vehicle.places,
                is_executive: vehicle.is_executive,
                requiresApprover: requiresApprover
            });
        },

        showVehicleDetails(vehicle) {
            this.$emit('show-vehicle-details', vehicle);
        }
    }
};
</script>

<style scoped>
.vehicle-selection {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 20px;
}

.vehicle-selection h4 {
    color: #495057;
    font-weight: 600;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
}

/* Seletor de origem do veículo (dois cards) */
.vehicle-type-selector {
    display: flex;
    gap: 12px;
    margin: 0 auto 24px;
    max-width: 480px;
}

.type-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 14px 8px;
    background: #ffffff;
    border: 1px solid #adb5bd;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    color: #495057;
    cursor: pointer;
    transition: all 0.2s ease;
}

.type-card:hover:not(.active) {
    border-color: #FE8D18;
}

.type-card.active {
    background: #FE8D18;
    border-color: #FE8D18;
    color: #ffffff;
}

.type-card-icon {
    font-size: 22px;
}

.type-card-label {
    font-size: 0.95rem;
    font-weight: 500;
    text-align: center;
}

.type-card.active .type-card-label {
    font-weight: 700;
}

.no-vehicles-message {
    margin-top: 20px;
}

.alert {
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.alert-info {
    background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
    color: #0c5460;
}

/* Responsividade */
@media (max-width: 768px) {
    .vehicle-selection {
        padding: 15px !important;
        margin-top: 15px;
    }

    .vehicle-selection h4 {
        font-size: 1.2rem;
    }

    .type-card {
        padding: 12px 6px;
    }

    .type-card-icon {
        font-size: 20px;
    }

    .type-card-label {
        font-size: 0.85rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/add-components/VehicleSelection.vue